// Droppable Slot Component
import React, {useEffect, useState} from "react";
import {useDroppable} from "@dnd-kit/core";
import {Home, ScanSearch, X} from "lucide-react";
import {SelectedComp} from "./configurableCompsSelectTypes.tsx";
import {SELECTED_TYPE} from "./CompsDragDropTable.tsx";
import {CompSelectType} from "../../DynamicFormTypes.ts";
import {emitEvent} from "../../../../context/EventBus.ts";
import {ImageSubmission} from "../../../../api/ApiTypes.ts";
import {useFileApi} from "../../../../context/ApiContext.tsx";
import {FileDownloadType} from "../../../FileViewer.tsx";

function getValue(comp: SelectedComp, key: string): any {
    if (!comp) {
        return undefined;
    }
    if (!comp.formValues) {
        return undefined;
    }
    let prefixedKey = comp.dgInfo.prefixForApiPopulation + key;
    if (comp.formValues.hasOwnProperty(prefixedKey)) {
        return comp.formValues[prefixedKey];
    }
}

function extractImageSubmission(data: any): ImageSubmission | undefined {
    if (Array.isArray(data) && data.length > 0) {
        return data[0] as ImageSubmission;
    }
    return undefined;
}

const DroppableCompSlotRenderer: React.FC<{
    index: number;
    selectedComp: SelectedComp;
    onRemove: () => void;
}> = ({ index, selectedComp, onRemove }) => {
    const { setNodeRef, isOver, active } = useDroppable({
        id: `${SELECTED_TYPE}-${index}`,
        data: { type: SELECTED_TYPE, SELECTED_TYPE: selectedComp }
    });

    const fileApi = useFileApi();
    const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);

    // Handle remove with event prevention
    const handleRemove = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onRemove();
    };

    const address = getValue(selectedComp, "Address");
    const externalImageUrl = getValue(selectedComp, "ImageUrl");
    const prosConsData = getValue(selectedComp, "ProsCons");

    // Fetch uploaded image if no external URL exists
    useEffect(() => {
        const fetchUploadedImage = async () => {
            // Only fetch if no external image URL exists
            if (externalImageUrl) {
                setUploadedImageUrl(null);
                return;
            }

            const imageSubmission = extractImageSubmission(prosConsData);
            if (!imageSubmission?.generalFileId) {
                setUploadedImageUrl(null);
                return;
            }

            try {
                const fileDownload = await fileApi.downloadFile(imageSubmission.generalFileId, FileDownloadType.GENERAL_FILE);
                // Create data URL from base64 string
                const dataUrl = `data:${fileDownload.contentType};base64,${fileDownload.data}`;
                setUploadedImageUrl(dataUrl);
            } catch (error) {
                console.error('Failed to load uploaded image:', error);
                setUploadedImageUrl(null);
            }
        };

        fetchUploadedImage();

    }, [externalImageUrl, prosConsData, fileApi]);

    // To Open Flyout
    const handleClickComp = (): void => {
        emitEvent("COMPARABLE_CLICKED", {
            fieldKey: selectedComp.dgInfo.dataGridFieldKey,
            index: selectedComp.indexOfDg
        });
    };

    // To Open Comp CA Image Modal
    const handleClickCompImage = (): void => {
        emitEvent("COMPARABLE_IMAGE_CLICKED", {
            fieldKey: selectedComp.dgInfo.dataGridFieldKey,
            index: selectedComp.indexOfDg
        });
    };

    if (address) {
        const displayImageUrl = externalImageUrl || uploadedImageUrl;
        const address = getValue(selectedComp, "Address");
        const mlsId = getValue(selectedComp, "MlsId");
        const listPrice = getValue(selectedComp, "LastSoldPrice") != null ? getValue(selectedComp, "LastSoldPrice") : getValue(selectedComp, "ListPrice");
        return (
            <div className={`relative bg-white border ${selectedComp.dgInfo.requiredType === CompSelectType.ACTIVE ? "border-green-400" : "border-blue-300"} rounded-lg shadow-sm h-60 overflow-hidden`}>
                <button
                    type="button"
                    onClick={handleRemove}
                    className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md hover:bg-red-600 transition-colors"
                >
                    <X size={14} />
                </button>

                {/* Whole div is clickable to open flyout */}
                <div className="p-3 cursor-pointer" onClick={()=> handleClickComp()}>
                    <div className="h-36 w-full overflow-hidden mb-2 flex items-center justify-center relative">
                        {displayImageUrl ? (
                            <>
                                <img
                                    src={displayImageUrl}
                                    alt={address}
                                    className="w-full h-full object-cover rounded-lg"
                                />
                                {/* This button opens Comp CA Image Modal */}
                                <button
                                    className="absolute bottom-0 right-0 bg-black/20 hover:bg-black/50 p-1 rounded-full transition-colors"
                                    onClick={(e) => {
                                        handleClickCompImage();
                                        e.preventDefault();
                                    }}                                >
                                    <ScanSearch className="h-4 w-4 text-white"/>
                                </button>
                            </>
                        ) : (
                            <Home className="text-gray-400" size={32} />
                        )}
                    </div>
                    <div className="text-xs text-gray-500 mb-1">MLS# {mlsId}</div>
                    <div className="text-sm font-medium truncate" title={address}>
                        {address}
                    </div>
                    <div className="text-xs text-blue-600 font-semibold mt-1">
                        ${listPrice ? new Intl.NumberFormat().format(listPrice) : '-'}
                    </div>
                </div>
            </div>
        );
    }

    const activeSelectableComp = active?.data?.current?.SELECTABLE_TYPE as SelectedComp | null;
    if (isOver && activeSelectableComp && activeSelectableComp.dgInfo.requiredType !== selectedComp.dgInfo.requiredType) {
        return (
            <div
                className="border-2 border-red-400 bg-red-50 rounded-lg h-48 flex flex-col items-center justify-center"
            >
                <Home className="text-red-400 mb-2" size={24} />
                <span className="text-sm font-medium text-red-600">Invalid Type</span>
                <span className="text-xs text-red-500">Expected: {selectedComp.dgInfo.requiredType}</span>
            </div>
        )
    }

    return (
        <div
            ref={setNodeRef}
            className={`
                border-2 border-dashed rounded-lg h-48 flex flex-col items-center justify-center
                transition-all ${isOver ? 'border-gray-500 border-3 border-solid bg-white' : 'border-gray-300'}
                ${selectedComp.dgInfo.requiredType == CompSelectType.ACTIVE ? "bg-green-50" : ""}
                ${selectedComp.dgInfo.requiredType == CompSelectType.SOLD ? "bg-blue-50" : ""}
                ${selectedComp.dgInfo.requiredType == CompSelectType.ANY ? "bg-gray-50" : ""}
            `}
        >
            <Home className="text-gray-400 mb-2" size={24} />
            <span className="text-sm font-medium text-gray-600">Comp #{index + 1}</span>
            <span className="text-sm font-bold text-gray-600">{selectedComp.dgInfo.requiredType}</span>

            <p className="text-xs text-gray-500 mt-1 text-center">Drag from table below</p>
        </div>
    );
}

export const DroppableCompSlot = React.memo(DroppableCompSlotRenderer);
